import { POSITION_LABELS } from "@board/positions";
import {
  BrianBubble,
  cn,
  EmptyNote,
  OrderLine,
  PatientHeader,
  PatientSprite,
  Skeleton,
} from "@scribenote/brian-design-system";
import { DisplayFrame } from "@shell/DisplayFrame";
import { ViewRenderer } from "@views/ViewRenderer";
import type { ReactNode } from "react";

import {
  awayLine,
  bookingLine,
  joinDots,
  lastVisitLine,
  ownerLine,
  patientFullName,
  signalmentLine,
  weightText,
} from "./examRoomFormat";
import type {
  ConfirmedSheet,
  PendingSheet,
  PlanLine,
  RequestedView,
  RoomQuestion,
  Sheet,
} from "./examRoomView";
import { useQuestionChime } from "./useQuestionChime";

const SHEET =
  "rounded-paper border border-rule bg-card px-10 pt-8 pb-9 shadow-sheet";
const chrome = (narrow: boolean) =>
  ({
    sheet: cn(SHEET, narrow && "min-w-0 px-6 pt-6 pb-7"),
    header: narrow ? "card" : "display",
    fields: narrow ? "grid grid-cols-1 gap-5" : "grid grid-cols-3 gap-10",
  }) as const;
const HEAD = "text-mono-caps text-[0.8125rem] text-ink-body";
const BODY = "mt-1.5 text-[1.1875rem] leading-[1.35] text-ink";

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="min-w-0">
    <h2 className={HEAD}>{label}</h2>
    <p className={BODY}>{children}</p>
  </div>
);

const PLACEHOLDER_FIELDS = ["Reason", "History", "Last visit"];

// Shapes only: a pending sheet's payload has no record to blur.
const HiddenRecord = ({ narrow }: { narrow: boolean }) => (
  <div className="mt-6 border-t border-ink pt-5">
    <p className="text-mono-caps text-xs text-annotation">
      Hidden until confirmed
    </p>
    <div
      aria-hidden="true"
      className="mt-4 flex select-none flex-col gap-6 blur-[3px]"
    >
      <div className={chrome(narrow).fields}>
        {PLACEHOLDER_FIELDS.map((label) => (
          <div key={label} className="flex flex-col gap-2">
            <span className={HEAD}>{label}</span>
            <span className="h-4 w-11/12 rounded-paper bg-recess" />
            <span className="h-4 w-2/3 rounded-paper bg-recess" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

const PendingSheetView = ({
  sheet,
  narrow,
}: {
  sheet: PendingSheet;
  narrow: boolean;
}) => (
  <article
    data-testid="exam-room-sheet-pending"
    data-dd-privacy="hidden"
    className={chrome(narrow).sheet}
  >
    <PatientHeader
      size={chrome(narrow).header}
      name={
        <span className="rounded-swipe bg-fill-attention px-2 blend-marker box-decoration-clone">
          {patientFullName(sheet.header)}
        </span>
      }
      meta={joinDots(signalmentLine(sheet.header), bookingLine(sheet.header))}
      sprite={sheet.header.sprite}
    />
    <p className="mt-3 text-mono-caps text-sm text-ink-body">
      {POSITION_LABELS[sheet.position]}
    </p>
    <HiddenRecord narrow={narrow} />
  </article>
);

const Plan = ({ plan, narrow }: { plan: PlanLine[]; narrow: boolean }) => (
  <section className="mt-7">
    <h2 className={cn(HEAD, "border-b border-ink pb-2")}>
      {joinDots(
        "Plan",
        `${plan.length} ${plan.length === 1 ? "order" : "orders"}`,
      )}
    </h2>
    {plan.length === 0 ? (
      <EmptyNote className="justify-start px-0 py-4 text-[1.1875rem]">
        Nothing on the plan yet.
      </EmptyNote>
    ) : (
      <ul>
        {plan.map((line) => (
          <OrderLine
            key={line.orderId}
            status={line.chip}
            route={line.route}
            time={line.at}
            dense={narrow}
          >
            {line.text}
          </OrderLine>
        ))}
      </ul>
    )}
  </section>
);

const ConfirmedSheetView = ({
  sheet,
  narrow,
}: {
  sheet: ConfirmedSheet;
  narrow: boolean;
}) => {
  const fields = [
    { label: "Reason", value: sheet.reason },
    { label: "History", value: sheet.history },
    {
      label: "Last visit",
      value: sheet.lastVisit && lastVisitLine(sheet.lastVisit),
    },
  ].filter((field) => field.value);

  return (
    <article
      data-testid="exam-room-sheet-confirmed"
      data-dd-privacy="hidden"
      className={chrome(narrow).sheet}
    >
      <PatientHeader
        size={chrome(narrow).header}
        name={patientFullName(sheet.header)}
        meta={joinDots(
          signalmentLine(sheet.header),
          sheet.weight && weightText(sheet.weight),
          sheet.header.dvmName,
          sheet.owner && ownerLine(sheet.owner),
          awayLine(sheet.awaySince, sheet.awayIn),
        )}
        sprite={sheet.header.sprite}
      />
      <p className="mt-3 text-mono-caps text-sm text-ink-body">
        {POSITION_LABELS[sheet.position]}
      </p>
      {fields.length > 0 && (
        <div
          className={cn(chrome(narrow).fields, "mt-6 border-t border-ink pt-5")}
        >
          {fields.map((field) => (
            <Field key={field.label} label={field.label}>
              {field.value}
            </Field>
          ))}
        </div>
      )}
      <Plan plan={sheet.plan} narrow={narrow} />
    </article>
  );
};

const REQUESTED_LABEL: Record<RequestedView["kind"], string> = {
  patient_chart: "Patient chart",
  bloodwork: "Bloodwork",
};

const RequestedPanel = ({ view }: { view: RequestedView }) => (
  <section
    data-testid="exam-room-requested-view"
    data-dd-privacy="hidden"
    aria-label={REQUESTED_LABEL[view.kind]}
    className={cn(SHEET, "border-ink")}
  >
    <h2 className="font-display text-[2.25rem] font-medium leading-tight text-ink">
      {joinDots(REQUESTED_LABEL[view.kind], view.patientName)}
    </h2>
    <div className="mt-5 border-t border-ink pt-5">
      {view.summary ? (
        <ViewRenderer view={view.summary} frameless />
      ) : (
        <EmptyNote className="justify-start px-0 py-4 text-[1.1875rem]">
          No Summary &amp; Alerts on file for {view.patientName}.
        </EmptyNote>
      )}
    </div>
  </section>
);

// Read-only: staff answer from their app or by voice, never on the wall (BRI-379).
const RoomQuestionPanel = ({ question }: { question: RoomQuestion }) => (
  <BrianBubble
    variant="asking"
    tail="start"
    data-testid="exam-room-question"
    data-dd-privacy="hidden"
    className="shrink-0"
    drawing={
      <ul className="flex flex-wrap gap-x-10 gap-y-4">
        {question.candidates.map((candidate) => (
          <li
            key={candidate.visitId}
            className="flex items-center gap-4 font-display text-[1.625rem] font-medium text-ink"
          >
            <PatientSprite sprite={candidate.sprite} size={56} />
            {candidate.label}
          </li>
        ))}
      </ul>
    }
  >
    {question.prompt}
  </BrianBubble>
);

// One pet fills the screen, two split it, three take thirds, a litter gets quadrants (BRI-377).
const columnsFor = (count: number): string =>
  count === 3 ? "grid-cols-3" : count > 1 ? "grid-cols-2" : "grid-cols-1";

const ExamRoomSheets = ({
  sheets,
  requestedView = null,
  question = null,
}: {
  sheets: Sheet[];
  requestedView?: RequestedView | null;
  question?: RoomQuestion | null;
}) => {
  if (sheets.length === 0 && !requestedView && !question)
    return (
      <p
        data-testid="exam-room-clear"
        className="m-auto font-display text-[2.5rem] font-medium text-annotation"
      >
        Room clear
      </p>
    );

  const narrow = sheets.length > 1;
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto pb-1">
      {question && <RoomQuestionPanel question={question} />}
      {requestedView && <RequestedPanel view={requestedView} />}
      <div
        data-testid="exam-room-sheets"
        className={cn("grid items-start gap-6", columnsFor(sheets.length))}
      >
        {sheets.map((sheet) =>
          sheet.state === "pending" ? (
            <PendingSheetView
              key={sheet.visitId}
              sheet={sheet}
              narrow={narrow}
            />
          ) : (
            <ConfirmedSheetView
              key={sheet.visitId}
              sheet={sheet}
              narrow={narrow}
            />
          ),
        )}
      </div>
    </div>
  );
};

export const ExamRoomDisplay = ({
  roomName,
  sheets,
  requestedView = null,
  question = null,
}: {
  roomName: string;
  sheets: Sheet[] | null;
  requestedView?: RequestedView | null;
  question?: RoomQuestion | null;
}) => {
  useQuestionChime(question?.questionId ?? null);

  return (
    <DisplayFrame tapes={[roomName]}>
      {sheets ? (
        <ExamRoomSheets
          sheets={sheets}
          requestedView={requestedView}
          question={question}
        />
      ) : (
        <div aria-busy="true" className="flex flex-col">
          <Skeleton shape="sheet" className="h-[30rem]" />
        </div>
      )}
    </DisplayFrame>
  );
};
